import { useEffect, useState } from 'react'
import { UploadSimple, Lock, Check, X, Clock, Users, ArrowsClockwise, CheckCircle, XCircle } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useCrmExportApprovalsStore } from '@/stores/crmExportApprovalsStore'
import { usePermission } from '@/hooks/usePermission'
import { cn } from '@/lib/utils'
import { cardChromeClass, iconChipClass, iconChipAmberClass } from '@/lib/styleKit'

const PROVIDER_LABELS: Record<string, string> = {
  hubspot: 'HubSpot',
  salesforce: 'Salesforce',
  pipedrive: 'Pipedrive',
  zoho: 'Zoho CRM',
}

export default function CrmExportApprovalsSection() {
  const { pending, decided = [], loading, fetchPending, approve, reject } = useCrmExportApprovalsStore()
  const { allowed, scope } = usePermission('contacts', 'bulk_export')
  // A Rep's own level for this permission is "gated" (truthy, so `allowed`
  // alone would let them in) -- "gated" means "may request an export of their
  // own contacts", never "may approve one". Only "all" (Owner/Admin/Manager)
  // may see or act on this queue. Mirrors CampaignApprovalsSection.
  const canApprove = allowed && scope === 'all'
  const [actingOn, setActingOn] = useState<number | null>(null)

  useEffect(() => {
    if (!canApprove) return
    fetchPending().catch(() => {})
  }, [canApprove, fetchPending])

  const handleApprove = async (requestId: number) => {
    setActingOn(requestId)
    try {
      await approve(requestId)
    } catch {
      // Error already toasted by the store.
    } finally {
      setActingOn(null)
    }
  }

  const handleReject = async (requestId: number) => {
    setActingOn(requestId)
    try {
      await reject(requestId)
    } catch {
      // Error already toasted by the store.
    } finally {
      setActingOn(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          CRM Export Approvals
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          {canApprove
            ? 'Reps need your approval before their contacts can be uploaded to a CRM. Approving uploads them right away, through your CRM connection.'
            : 'Your role determines what you can see and do in this account.'}
        </p>
      </div>

      {!canApprove ? (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipAmberClass}>
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div>
                <CardTitle>No access</CardTitle>
                <CardDescription>
                  Your role doesn't include reviewing CRM export requests
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className={iconChipClass}>
                  <UploadSimple className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                </div>
                <div>
                  <CardTitle>Pending requests</CardTitle>
                  <CardDescription>Contacts a Representative asked to push to a CRM</CardDescription>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                loading={loading}
                onClick={() => fetchPending().catch(() => {})}
                icon={<ArrowsClockwise className="w-3.5 h-3.5" weight="regular" />}
                aria-label="Refresh pending requests"
              >
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pending.length === 0 ? (
              <p
                className="text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
              >
                {loading ? 'Loading…' : 'No pending export requests.'}
              </p>
            ) : (
              <div className="space-y-2">
                {pending.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] flex-wrap"
                  >
                    <div className="min-w-0">
                      <p
                        className="text-foreground dark:text-[#f7f8f8] truncate"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        {req.source} → {PROVIDER_LABELS[req.provider] || req.provider}
                      </p>
                      {/* Where the contacts are going and how many is what an
                          approver is actually signing off on. The count is as
                          it stood when asked -- a list is re-counted on upload. */}
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] flex items-center gap-2.5 flex-wrap mt-0.5"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        <span className="flex items-center gap-1.5">
                          <Users className="w-3 h-3" />
                          {req.contact_count.toLocaleString()} contact{req.contact_count === 1 ? '' : 's'}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          Requested by {req.requested_by_name}
                          {req.requested_at ? ` on ${new Date(req.requested_at).toLocaleString()}` : ''}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={actingOn === req.id}
                        icon={<X className="w-3.5 h-3.5 text-destructive" />}
                        onClick={() => handleReject(req.id)}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        loading={actingOn === req.id}
                        icon={<Check className="w-3.5 h-3.5" />}
                        onClick={() => handleApprove(req.id)}
                      >
                        Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {canApprove && decided.length > 0 && (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipClass}>
                <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" weight="fill" />
              </div>
              <div>
                <CardTitle>Recently decided</CardTitle>
                <CardDescription>Who approved or rejected each request</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {decided.map((req) => {
                const approved = req.status === 'approved'
                return (
                  <div
                    key={req.id}
                    data-testid={`decided-${req.id}`}
                    className={
                      approved
                        ? 'px-3 py-2.5 rounded-md border bg-emerald-500/[0.06] border-emerald-500/25 dark:bg-emerald-500/[0.10] dark:border-emerald-500/25'
                        : 'px-3 py-2.5 rounded-md border bg-red-500/[0.05] border-red-500/20 dark:bg-red-500/[0.08] dark:border-red-500/20'
                    }
                  >
                    <p
                      className="text-foreground dark:text-[#f7f8f8] truncate"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      {req.source} → {PROVIDER_LABELS[req.provider] || req.provider}
                    </p>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] flex items-center gap-2.5 flex-wrap mt-0.5"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      <span
                        className={cn(
                          'flex items-center gap-1',
                          approved ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
                        )}
                        style={{ fontWeight: 510 }}
                      >
                        {approved ? <CheckCircle className="w-3 h-3" weight="fill" /> : <XCircle className="w-3 h-3" weight="fill" />}
                        {approved ? 'Approved' : 'Rejected'} by {req.decided_by_name || 'Unknown'}
                        {req.decided_at ? ` on ${new Date(req.decided_at).toLocaleString()}` : ''}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users className="w-3 h-3" />
                        {req.contact_count.toLocaleString()} contact{req.contact_count === 1 ? '' : 's'}, requested by {req.requested_by_name}
                      </span>
                    </p>
                    {!approved && req.reason && (
                      <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12px' }}>
                        Reason: {req.reason}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
